<template>
  <NoCustomerState v-if="!currentFilters.customerId" :hasCustomers="hasCustomers" />
  <div v-else class="tw-flex tw-flex-col tw-gap-8">
    <DashboardFilter @update:filters="handleFiltersChange" />
    <div class="tw-flex tw-flex-row tw-gap-8">
      <div class="tw-w-[40%] tw-h-full">
        <DashboardCards
          :startDate="currentFilters.startDate"
          :endDate="currentFilters.endDate"
          :customerId="currentFilters.customerId"
        />
      </div>
      <div class="tw-w-full tw-h-full">
        <InocuBarChart :series="series" :xaxis="xaxis" :isLoading="isLoading" :height="400">
          <template #title></template>
        </InocuBarChart>
      </div>
    </div>
    <div class="tw-grid tw-grid-cols-2 tw-gap-8">
      <UserTable />
      <CustomersTable />
    </div>
  </div>
</template>

<script setup lang="ts">
import DashboardFilter from './DashboardFilter.vue';
import DashboardCards from './DashboardCards.vue';
import InocuBarChart from '@/components/shared/charts/InocuBarChart.vue';
import UserTable from './UserTable.vue';
import CustomersTable from './CustomersTable.vue';
import NoCustomerState from '@/components/shared/NoCustomerState.vue';
import { ref, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useDashboardStore } from '@/stores/dashboard.store';
import { useCustomerStore } from '@/stores/customer.store';

const { getNumberOfRequests } = useDashboardStore();
const { hasCustomers, selectedCustomerId } = storeToRefs(useCustomerStore());

const { t } = useI18n();

const isLoading = ref(true);
const currentFilters = ref<{
  startDate: string;
  endDate: string;
  customerId: number | null;
}>({
  startDate: '',
  endDate: '',
  customerId: null,
});

const series: Ref<{ name: string; data: number[] }[]> = ref([
  {
    name: t('statistics.titles.total_payment_requests'),
    data: [],
  },
  {
    name: t('statistics.titles.total_order_requests'),
    data: [],
  },
]);
const xaxis: Ref<string[]> = ref([]);

// Watch for customer ID changes from store
watch(
  selectedCustomerId,
  newCustomerId => {
    if (newCustomerId && newCustomerId > 0) {
      currentFilters.value.customerId = newCustomerId;
    }
  },
  { immediate: true }
);

const fetchData = async () => {
  // Don't fetch if customer ID is not set
  if (!currentFilters.value.customerId) {
    return;
  }

  isLoading.value = true;
  try {
    const data = await getNumberOfRequests(
      currentFilters.value.startDate,
      currentFilters.value.endDate,
      currentFilters.value.customerId
    );
    series.value[0].data = data.payments;
    series.value[1].data = data.orders;
    xaxis.value = data.dates;
  } catch (error) {
    console.error('Error fetching number of requests:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleFiltersChange = async (filters: any) => {
  currentFilters.value = filters;
  await fetchData();
};
</script>
